/**
 * useDeadlines.js
 * Fetches the pool deadlines from the public /api/deadlines endpoint.
 * Returns deadline dates and helper flags.
 */

import { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

let cache = null;

// Call this after admin saves deadlines to force a fresh fetch
export function invalidateDeadlinesCache() {
  cache = null;
}

export function useDeadlines() {
  const [deadlines, setDeadlines] = useState(cache || { playInDeadline: null, regularDeadline: null, loaded: false });

  useEffect(() => {
    if (cache) { setDeadlines(cache); return; }
    fetch(`${API_BASE}/deadlines`)
      .then(r => r.json())
      .then(data => {
        const d = {
          playInDeadline:  data.playInDeadline  || null,
          regularDeadline: data.regularDeadline || null,
          loaded: true,
        };
        cache = d;
        setDeadlines(d);
      })
      .catch(() => setDeadlines({ playInDeadline: null, regularDeadline: null, loaded: true }));
  }, []);

  const now = new Date();
  const playInDate  = deadlines.playInDeadline  ? new Date(deadlines.playInDeadline)  : null;
  const regularDate = deadlines.regularDeadline ? new Date(deadlines.regularDeadline) : null;
  const MS_24H = 24 * 60 * 60 * 1000;

  return {
    ...deadlines,
    playInDate,
    regularDate,
    pastPlayIn:   playInDate  ? now > playInDate  : false,
    pastRegular:  regularDate ? now > regularDate : false,
    // Show regular deadline alert only within 24hrs after it passed
    showRegularAlert: regularDate ? (now > regularDate && now < new Date(regularDate.getTime() + MS_24H)) : false,
    // Show play-in alert from when it passes until the regular deadline passes
    showPlayInAlert:  playInDate && regularDate ? (now > playInDate && now < regularDate) : (playInDate ? now > playInDate : false),
    playInLabel:  playInDate  ? playInDate.toLocaleString()  : null,
    regularLabel: regularDate ? regularDate.toLocaleString() : null,
  };
}
